import { useEffect, useMemo, useState } from 'react'
import { BIG_0, ChainId, Currency, JSBI, Price, WNATIVE } from '@nominex/nomiswap-sdk'
import { BUSD, NMX, USDC, USDT } from 'config/tokens'
import { useWagmi } from 'libs/wagmi'
import { infoApi } from 'restApi'
import { PairState, usePairs } from './usePairs'

/**
 * Returns the price in USD of the input currency
 * @param currency currency to compute the USD price of
 * TODO: should be refactored
 */
export default function useUsdtPrice(currency?: Currency): Price<Currency, Currency> | undefined {
  const { chainId } = useWagmi()
  const wrappedToken = currency?.wrapped

  const tokenPairs: [Currency | undefined, Currency | undefined][] = useMemo(
    () => [
      [
        chainId && wrappedToken && WNATIVE[chainId]?.equals(wrappedToken) ? undefined : currency,
        chainId ? WNATIVE[chainId] : undefined,
      ],
      [
        chainId && USDT[chainId] && wrappedToken?.equals(USDT[chainId]) ? undefined : wrappedToken,
        chainId ? USDT[chainId] : undefined,
      ],
      [
        chainId && USDC[chainId] && wrappedToken?.equals(USDC[chainId]) ? undefined : wrappedToken,
        chainId ? USDC[chainId] : undefined,
      ],
      [
        chainId && BUSD[chainId] && wrappedToken?.equals(BUSD[chainId]) ? undefined : wrappedToken,
        chainId ? BUSD[chainId] : undefined,
      ],
      [chainId ? WNATIVE[chainId] : undefined, chainId ? USDT[chainId] : undefined],
    ],
    [chainId, currency, wrappedToken],
  )
  const [
    [wnativePairState, wnativePair],
    [usdtPairState, usdtPair],
    [usdcPairState, usdcPair],
    [busdPairState, busdPair],
    [wnativeUsdtPairState, wnativeUsdtPair],
  ] = usePairs(tokenPairs)

  return useMemo(() => {
    // Guard against undefined chainId or missing token configurations
    if (!chainId || !USDT[chainId] || !WNATIVE[chainId]) {
      return undefined
    }
    if (!currency || !wrappedToken || !chainId) {
      return undefined
    }

    // handle usdt
    if (wrappedToken.equals(USDT[chainId])) {
      return new Price(USDT[chainId], USDT[chainId], '1', '1')
    }

    // handle native currency
    if (wrappedToken.equals(WNATIVE[chainId])) {
      if (usdtPair) {
        const price = usdtPair.priceOf(WNATIVE[chainId])
        return new Price(currency, USDT[chainId], price.denominator, price.numerator)
      }
      return undefined
    }

    const wnativePairReserve = wnativePair?.reserveOf(WNATIVE[chainId])
    const wnativePairQuote: JSBI =
      wnativePairReserve && wnativeUsdtPair
        ? wnativeUsdtPair.priceOf(WNATIVE[chainId]).quote(wnativePairReserve).quotient
        : BIG_0

    // all other tokens
    // first try the usdt pair
    if (
      usdtPairState === PairState.EXISTS &&
      usdtPair &&
      usdtPair.reserveOf(USDT[chainId]).greaterThan(wnativePairQuote)
    ) {
      const price = usdtPair.priceOf(wrappedToken)
      return new Price(currency, USDT[chainId], price.denominator, price.numerator)
    }

    if (
      usdcPairState === PairState.EXISTS &&
      usdcPair &&
      usdcPair.reserveOf(USDC[chainId]).greaterThan(wnativePairQuote)
    ) {
      const price = usdcPair.priceOf(wrappedToken)
      return new Price(currency, USDC[chainId], price.denominator, price.numerator)
    }

    if (
      busdPairState === PairState.EXISTS &&
      busdPair &&
      busdPair.reserveOf(BUSD[chainId]).greaterThan(wnativePairQuote)
    ) {
      const price = busdPair.priceOf(wrappedToken)
      return new Price(currency, BUSD[chainId], price.denominator, price.numerator)
    }

    if (
      wnativePairState === PairState.EXISTS &&
      wnativePair &&
      wnativeUsdtPairState === PairState.EXISTS &&
      wnativeUsdtPair
    ) {
      if (
        wnativeUsdtPair.reserveOf(USDT[chainId]).greaterThan('0') &&
        wnativePair.reserveOf(WNATIVE[chainId]).greaterThan('0')
      ) {
        const wnativeUsdtPrice = wnativeUsdtPair.priceOf(USDT[chainId])
        const wnativePrice = wnativePair.priceOf(WNATIVE[chainId])
        const usdtPrice = wnativeUsdtPrice.multiply(wnativePrice).invert()
        return new Price(currency, USDT[chainId], usdtPrice.denominator, usdtPrice.numerator)
      }
    }

    return undefined
  }, [
    chainId,
    currency,
    wnativePair,
    wnativePairState,
    wnativeUsdtPair,
    wnativeUsdtPairState,
    usdtPair,
    usdtPairState,
    usdcPair,
    usdcPairState,
    busdPair,
    busdPairState,
    wrappedToken,
  ])
}

export const useNmxUsdtPrice = (): Price<Currency, Currency> | undefined => {
  const { chainId } = useWagmi()
  const nmx = NMX[chainId]
  const usdt = USDT[chainId]
  const blockChainValue = useUsdtPrice(nmx)
  const fallbackValue = useNmxPriceFallback(chainId)

  // Guard against undefined tokens
  if (!nmx || !usdt) {
    return undefined
  }

  const nmxFallbackPrice = new Price(nmx, usdt, 10 ** nmx.decimals, Math.floor(fallbackValue * 10 ** usdt.decimals))

  return chainId === ChainId.BSC ? blockChainValue : nmxFallbackPrice
}

export const useNmxPriceFallback = (chainId: ChainId): number => {
  const [nmxPriceFallback, setNmxPriceFallback] = useState(0)

  useEffect(() => {
    const fetchNmxPrice = async () => {
      try {
        const response = await infoApi.getNmxPrice()
        setNmxPriceFallback(Number(response))
      } catch (e: any) {
        e.json().then(console.error)
      }
    }

    if (chainId !== ChainId.BSC) {
      fetchNmxPrice()

      const pollingInterval = setInterval(fetchNmxPrice, 30000)

      return () => clearInterval(pollingInterval)
    }

    return () => {}
  }, [chainId])

  return nmxPriceFallback
}
